import React from "react";
import {
    Stack,
    Text,
    createListTableColumns,
    ensure,
    ListTableColumn,
    renderDateTime,
    depascalize
} from "sprint-common-web";

import { renderEncodeSpecificationType } from "../../../common/utils/renderEncodeSpecificationType";
import { EncodeSpecificationType, EncodeSpecificationFramerate, AspectType } from "../context/EncodeSpecificationItem";
import { CloneEncodeSpecificationButton } from "./buttons/CloneEncodeSpecificationButton";
import { EditEncodeSpecificationButton } from "./buttons/EditEncodeSpecificationButton";
import { ShowFfmpegCommandButton } from "./buttons/ShowFfmpegCommandButton";
import { EncodeSpecificationListingItem } from "./EncodeSpecificationListingItem";

export const createEncodeSpecificationListingColumns = (): ListTableColumn<EncodeSpecificationListingItem>[] => {
    return createListTableColumns<EncodeSpecificationListingItem>([
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 100,
            maxWidth: 200,
            isSortable: true
        },
        {
            type: "basic",
            key: "type",
            name: "Type",
            minWidth: 100,
            maxWidth: 200,
            isSortable: true,
            onRender: renderEncodeSpecificationType
        },
        {
            type: "basic",
            key: "details",
            name: "Details",
            minWidth: 400,
            maxWidth: 700,
            onRender: renderDetails
        },
        {
            type: "basic",
            key: "targetAspectRatio",
            name: "Aspect ratio",
            minWidth: 100,
            maxWidth: 200,
            onRender: renderTargetRatio
        },
        {
            type: "basic",
            key: "updatedDate",
            name: "Updated",
            minWidth: 160,
            maxWidth: 360,
            isSortable: true,
            onRender: renderDateTime
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 100,
            maxWidth: 120,
            buttons: [
                {
                    id: "EditSpecification",
                    render: EditEncodeSpecificationButton
                },
                {
                    id: "CloneSpecification",
                    render: CloneEncodeSpecificationButton
                },
                {
                    id: "ShowFfmpegCommand",
                    render: ShowFfmpegCommandButton
                }
            ]
        }
    ]);
};

const renderTargetRatio = (item?: EncodeSpecificationListingItem) => {
    ensure(item);

    return <Text variant="small" value={item.targetAspectRatio.name} />;
};

const renderDetails = (item?: EncodeSpecificationListingItem) => {
    ensure(item);

    switch (item.type) {
        case EncodeSpecificationType.MPEG_1: {
            return (
                <Stack tokens={{ childrenGap: 5 }}>
                    {Object.keys(item.details.frameratePresets)
                        .map((framerate) => framerate as EncodeSpecificationFramerate)
                        .sort((a, b) => {
                            const numA = Number(a);
                            const numB = Number(b);

                            if (Number.isNaN(numA) && Number.isNaN(numB)) return 0;
                            if (Number.isNaN(numA)) return 1;
                            if (Number.isNaN(numB)) return -1;

                            return numA - numB;
                        })
                        .map((framerate) => (
                            <Stack key={framerate} horizontal={true} tokens={{ childrenGap: 7 }}>
                                <Text variant="smallPlus">{depascalize(framerate.toString())}:</Text>
                                <Text variant="smallPlus">{item.details.frameratePresets[framerate].name}</Text>
                            </Stack>
                        ))}
                    <Stack horizontal={true} tokens={{ childrenGap: 7 }}>
                        <Text variant="smallPlus">Aspect type:</Text>
                        <Text variant="smallPlus">{depascalize(AspectType[item.details.aspectType])}</Text>
                    </Stack>
                    <Stack horizontal={true} tokens={{ childrenGap: 7 }}>
                        <Text variant="smallPlus">Slug:</Text>
                        <Text variant="smallPlus">{item.details.slug ?? "-"}</Text>
                    </Stack>
                </Stack>
            );
        }

        case EncodeSpecificationType.MPEG_4: {
            return (
                <Stack>
                    <Stack horizontal={true} tokens={{ childrenGap: 7 }}>
                        <Text variant="smallPlus">Ffmpeg command:</Text>
                        <Text variant="smallPlus">{item.details.ffmpegCommand}</Text>
                    </Stack>
                    <Stack horizontal={true} tokens={{ childrenGap: 7 }}>
                        <Text variant="smallPlus">Framerate profile:</Text>
                        <Text variant="smallPlus">{item.details.framerateProfile.name}</Text>
                    </Stack>
                </Stack>
            );
        }

        default:
            throw new Error("Incorrect encode specification type");
    }
};
